import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import { resizeImage } from '../../lib/image';
import { date, money } from '../../lib/format';
import SignaturePad from '../../components/SignaturePad';
import { registerFieldSW, enqueue, syncNow, pendingForJob, allPending, pendingCount, onPendingChange, uid } from '../../lib/fieldOffline';

const typeLabel = (t) => ({ sim_repair: 'Sim repair', sim_move: 'Simulator move', sim_upgrade: 'Simulator upgrade', site_visit: 'Site visit', other: 'Other' }[t] || t);
const statusLabel = (s) => ({ open: 'Open', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled' }[s] || s);
const addrLine = (a) => (a && (a.line1 || a.city) ? [a.line1, a.line2, a.city, a.state, a.postal].filter(Boolean).join(', ') + (a.floor ? ` · Floor ${a.floor}` : '') : null);
const emptyPartForm = () => ({ part_name: '', sku: '', unit_price: '', quantity: '1', ordered_from: '', notes: '' });

// Overlay queued (not-yet-synced) actions onto a freshly fetched job so pending work
// stays visible — including after an offline reload.
function applyPending(job, actions) {
  if (!job || !actions || !actions.length) return job;
  let j = { ...job };
  for (const a of actions) {
    if (a.kind === 'note') {
      j.notes = [{ id: a.id, body: a.body.body, author: a.meta?.author || 'You', created_at: new Date(a.createdAt).toISOString(), _pending: true }, ...(j.notes || [])];
    } else if (a.kind === 'photo') {
      j.photos = [...(j.photos || []), { id: a.id, kind: a.body.kind, _localSrc: a.body.image, _pending: true }];
    } else if (a.kind === 'start') {
      if (j.status === 'open') j.status = 'in_progress';
    } else if (a.kind === 'complete') {
      j.status = 'completed';
      j.completion = { summary: a.body.summary, signer_name: a.body.signer_name, signature_data: a.body.signature, completed_at: new Date(a.createdAt).toISOString(), _pending: true };
    } else if (a.kind === 'task') {
      j.tasks = (j.tasks || []).map((t) => (t.id === a.meta?.taskId ? { ...t, is_complete: a.meta.is_complete } : t));
    } else if (a.kind === 'task-add') {
      j.tasks = [...(j.tasks || []), { id: a.meta.tempId, title: a.body.title, notes: a.body.notes || null, is_complete: false, _pending: true }];
    } else if (a.kind === 'task-edit') {
      j.tasks = (j.tasks || []).map((t) => (t.id === a.meta.taskId ? { ...t, title: a.body.title, notes: a.body.notes || null } : t));
    } else if (a.kind === 'part-add') {
      const price = Number(a.body.unit_price) || 0, qty = Number(a.body.quantity) || 0;
      j.parts = [...(j.parts || []), { id: a.meta.tempId, part_name: a.body.part_name, sku: a.body.sku || null, unit_price: price, quantity: qty, total: Math.round(price * qty * 100) / 100, ordered_from: a.body.ordered_from || null, notes: a.body.notes || null, billed: false, on_invoice: false, _pending: true }];
    }
  }
  return j;
}

function FieldPhoto({ jobId, photo, onOpen }) {
  const [src, setSrc] = useState(photo._localSrc || null);
  useEffect(() => {
    if (photo._localSrc) { setSrc(photo._localSrc); return undefined; }
    let live = true;
    api(`/jobs/${jobId}/photos/${photo.id}`).then((d) => { if (live) setSrc(d.image_data); }).catch(() => {});
    return () => { live = false; };
  }, [jobId, photo.id, photo._localSrc]);
  return (
    <div className="fld-photo" onClick={() => src && onOpen(src)}>
      {src ? <img src={src} alt="" /> : <div className="fld-photo__ph">…</div>}
      <span className="fld-photo__tag">{photo.kind}{photo._pending ? ' · pending' : ''}</span>
    </div>
  );
}

export default function FieldHome() {
  const { user, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [newTask, setNewTask] = useState({ title: '', notes: '' });
  const [editingTask, setEditingTask] = useState(null); // { id, title, notes }
  const [partForm, setPartForm] = useState(emptyPartForm());
  const [showAddPart, setShowAddPart] = useState(false);
  const [photoKind, setPhotoKind] = useState('other');
  const [lightbox, setLightbox] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [summary, setSummary] = useState('');
  const [signer, setSigner] = useState('');
  const [compErr, setCompErr] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const photoRef = useRef(null);
  const sigRef = useRef(null);
  const jobRef = useRef(null);
  useEffect(() => { jobRef.current = job; }, [job]);

  const loadList = useCallback(async () => {
    setLoading(true);
    let list = [];
    try { const d = await api('/jobs/mine'); list = Array.isArray(d) ? d : []; }
    catch { list = []; }
    // Overlay any pending status/complete actions so the list reflects offline work.
    try {
      const queued = await allPending();
      const byJob = {};
      queued.forEach((a) => { (byJob[a.jobId] = byJob[a.jobId] || []).push(a); });
      list = list.map((j) => {
        const acts = byJob[j.id];
        if (!acts) return j;
        let status = j.status;
        if (acts.some((a) => a.kind === 'complete')) status = 'completed';
        else if (acts.some((a) => a.kind === 'start') && status === 'open') status = 'in_progress';
        return { ...j, status, _pending: true };
      });
    } catch { /* ignore */ }
    setJobs(list);
    setLoading(false);
    // When online, warm the cache of each job's detail so they open offline later.
    if (navigator.onLine) list.forEach((j) => { api(`/jobs/${j.id}`).catch(() => {}); });
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  // Service worker + connectivity + pending-count wiring.
  const flush = useCallback(async () => {
    const res = await syncNow();
    setPending(res.remaining);
    if (res.flushed) {
      const jid = jobRef.current?.id;
      if (jid) {
        const fresh = await api(`/jobs/${jid}`).catch(() => null);
        if (fresh) setJob(applyPending(fresh, await pendingForJob(jid)));
      }
      loadList();
    }
  }, [loadList]);
  const flushRef = useRef(flush);
  useEffect(() => { flushRef.current = flush; }, [flush]);

  useEffect(() => {
    registerFieldSW();
    pendingCount().then(setPending).catch(() => {});
    const off = onPendingChange(setPending);
    const goOnline = () => { setOnline(true); flushRef.current(); };
    const goOffline = () => setOnline(false);
    const onVis = () => { if (document.visibilityState === 'visible' && navigator.onLine) flushRef.current(); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVis);
    if (navigator.onLine) flushRef.current();
    return () => {
      off();
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  async function open(id) {
    setBusy(true);
    try {
      const fresh = await api(`/jobs/${id}`);
      setJob(applyPending(fresh, await pendingForJob(id)));
    } catch { /* offline with no cached copy */ } finally { setBusy(false); }
  }
  const reload = useCallback(async () => {
    const j = jobRef.current;
    if (!j) return;
    try {
      const fresh = await api(`/jobs/${j.id}`);
      setJob(applyPending(fresh, await pendingForJob(j.id)));
    } catch { /* keep current (likely offline) */ }
  }, []);
  function back() { setJob(null); setCompleting(false); loadList(); }

  // Queue a write, update the UI optimistically, and try to sync if we're online.
  async function submit(action) {
    setJob((j) => applyPending(j, [action]));
    await enqueue(action);
    if (navigator.onLine) await flush();
  }

  async function addNote() {
    const text = noteText.trim();
    if (!text || !job) return;
    setBusy(true);
    setNoteText('');
    try {
      await submit({ id: uid(), kind: 'note', jobId: job.id, url: `/jobs/${job.id}/notes`, method: 'POST', body: { body: text }, createdAt: Date.now(), meta: { author: user?.name || user?.email } });
    } finally { setBusy(false); }
  }

  async function toggleTask(t) {
    if (!job) return;
    const next = !t.is_complete;
    await submit({ id: uid(), kind: 'task', jobId: job.id, url: `/jobs/${job.id}/tasks/${t.id}`, method: 'PUT', body: { is_complete: next }, createdAt: Date.now(), meta: { taskId: t.id, is_complete: next } });
  }
  async function addFieldTask() {
    const title = newTask.title.trim();
    if (!title || !job) return;
    const notes = newTask.notes.trim() || null;
    setNewTask({ title: '', notes: '' });
    await submit({ id: uid(), kind: 'task-add', jobId: job.id, url: `/jobs/${job.id}/tasks`, method: 'POST', body: { title, notes }, createdAt: Date.now(), meta: { tempId: uid() } });
  }
  async function saveTaskEdit() {
    const e = editingTask;
    if (!e || !e.title.trim() || !job) return;
    setEditingTask(null);
    await submit({ id: uid(), kind: 'task-edit', jobId: job.id, url: `/jobs/${job.id}/tasks/${e.id}`, method: 'PUT', body: { title: e.title.trim(), notes: e.notes.trim() || null }, createdAt: Date.now(), meta: { taskId: e.id } });
  }
  async function addFieldPart() {
    const f = partForm;
    if (!f.part_name.trim() || !job) return;
    const body = {
      part_name: f.part_name.trim(), sku: f.sku.trim() || null,
      unit_price: Number(f.unit_price) || 0, quantity: Number(f.quantity) || 1,
      ordered_from: f.ordered_from.trim() || null, notes: f.notes.trim() || null,
    };
    setPartForm(emptyPartForm()); setShowAddPart(false);
    await submit({ id: uid(), kind: 'part-add', jobId: job.id, url: `/jobs/${job.id}/parts`, method: 'POST', body, createdAt: Date.now(), meta: { tempId: uid() } });
  }

  async function addPhotos(files) {
    if (!files?.length || !job) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const image = await resizeImage(file);
        await submit({ id: uid(), kind: 'photo', jobId: job.id, url: `/jobs/${job.id}/photos`, method: 'POST', body: { kind: photoKind, image }, createdAt: Date.now() });
      }
    } catch { /* ignore */ } finally { setBusy(false); }
  }
  async function start() {
    if (!job) return;
    setBusy(true);
    try {
      await submit({ id: uid(), kind: 'start', jobId: job.id, url: `/jobs/${job.id}/status`, method: 'POST', body: { status: 'in_progress' }, createdAt: Date.now() });
    } finally { setBusy(false); }
  }
  function openComplete() {
    setSummary(job.completion?.summary || '');
    setSigner(job.completion?.signer_name || '');
    setCompErr(null);
    setCompleting(true);
  }
  async function submitComplete() {
    setCompErr(null);
    if (!signer.trim()) { setCompErr('Enter the customer’s name.'); return; }
    if (sigRef.current?.isEmpty()) { setCompErr('Please capture the customer’s signature.'); return; }
    setBusy(true);
    try {
      const signature = sigRef.current.toDataURL();
      await submit({ id: uid(), kind: 'complete', jobId: job.id, url: `/jobs/${job.id}/complete`, method: 'POST', body: { summary: summary.trim() || null, signer_name: signer.trim(), signature }, createdAt: Date.now() });
      setCompleting(false);
    } catch (e) { setCompErr(e?.detail || 'Could not complete the job.'); }
    finally { setBusy(false); }
  }

  const offlineBar = (!online || pending > 0) ? (
    <div className={`fld-sync${online ? '' : ' fld-sync--off'}`}>
      <span>{online ? `${pending} change${pending === 1 ? '' : 's'} waiting to sync` : 'Offline — changes are saved on this device'}</span>
      {online && pending > 0 && <button className="fld-link" onClick={flush}>Sync now</button>}
    </div>
  ) : null;

  // ---- list view ----
  if (!job) {
    return (
      <div className="fld">
        <header className="fld-top">
          <div className="fld-top__brand"><span className="fld-mark" />Flightdeck Field</div>
          <button className="fld-link" onClick={logout}>Sign out</button>
        </header>
        {offlineBar}
        <div className="fld-body">
          <p className="fld-hello">Hi {user?.name || user?.email}. Your assigned jobs:</p>
          {loading ? <p className="fld-muted">Loading…</p> : jobs.length === 0 ? (
            <p className="fld-muted">No jobs assigned to you right now.</p>
          ) : (
            <div className="fld-list">
              {jobs.map((j) => (
                <button className={`fld-card${j.status === 'completed' ? ' fld-card--done' : ''}`} key={j.id} onClick={() => open(j.id)}>
                  <div className="fld-card__top">
                    <span className="fld-card__num">{j.job_number}</span>
                    <span className={`fld-pill fld-pill--${j.status}`}>{statusLabel(j.status)}</span>
                  </div>
                  <div className="fld-card__cust">{j.customer_name || '—'}</div>
                  <div className="fld-card__meta">{typeLabel(j.type)}{j.business_name ? ` · ${j.business_name}` : ''}{j.scheduled_date ? ` · ${date(j.scheduled_date)}` : ''}</div>
                  {j.location && <div className="fld-card__meta">{j.location}</div>}
                  {j._pending && <div className="fld-card__meta fld-card__meta--pending">Unsynced changes</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- detail view ----
  const mq = job.move_questionnaire;
  const done = job.status === 'completed';
  return (
    <div className="fld">
      <header className="fld-top">
        <button className="fld-link" onClick={back}>‹ Jobs</button>
        <div className="fld-top__title">{job.job_number}</div>
        <span className={`fld-pill fld-pill--${job.status}`}>{statusLabel(job.status)}</span>
      </header>
      {offlineBar}

      <div className="fld-body">
        <h1 className="fld-h1">{job.customer_name || 'Job'}</h1>
        <div className="fld-info">
          <div><span className="k">Type</span>{typeLabel(job.type)}{job.is_contract ? ' · Contract' : ''}</div>
          {job.scheduled_date && <div><span className="k">Scheduled</span>{date(job.scheduled_date)}</div>}
          {job.location && <div><span className="k">Location</span>{job.location}</div>}
          {job.customer_email && <div><span className="k">Customer</span><a href={`mailto:${job.customer_email}`}>{job.customer_email}</a></div>}
          {job.description && <div><span className="k">Details</span>{job.description}</div>}
          {addrLine(job.move_from) && <div><span className="k">From</span>{addrLine(job.move_from)}</div>}
          {addrLine(job.move_to) && <div><span className="k">To</span>{addrLine(job.move_to)}</div>}
          {job.staff?.length > 0 && <div><span className="k">Crew</span>{job.staff.map((s) => s.name || s.email).join(', ')}</div>}
        </div>

        {mq && (
          <section className="fld-sec">
            <h2>Pre-move details</h2>
            <div className="fld-info">
              {mq.simulator_type && <div><span className="k">Simulator</span>{mq.simulator_type}{mq.serial_number ? ` · S/N ${mq.serial_number}` : ''}</div>}
              {mq.new_room_size && <div><span className="k">New room</span>{mq.new_room_size}</div>}
              <div><span className="k">36&quot; doorways</span>{mq.doorways_36_certified ? `Certified${mq.certified_by ? ` by ${mq.certified_by}` : ''}` : 'Not certified'}</div>
              {mq.move_info && <div><span className="k">Move info</span>{mq.move_info}</div>}
              {mq.additional_work && <div><span className="k">Extra work</span>{mq.additional_work}</div>}
            </div>
          </section>
        )}

        {(job.type === 'site_visit' || (job.tasks || []).length > 0) && (
          <section className="fld-sec">
            <h2>Tasks {(job.tasks || []).length > 0 && <span className="fld-muted">{(job.tasks || []).filter((t) => t.is_complete).length}/{(job.tasks || []).length}</span>}</h2>
            {(job.tasks || []).length === 0 ? <p className="fld-muted">No tasks yet.</p> : (
              <div className="fld-tasks">
                {job.tasks.map((t) => (editingTask?.id === t.id ? (
                  <div className="fld-task fld-task--edit" key={t.id}>
                    <input className="fld-input" value={editingTask.title} onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })} placeholder="Task" />
                    <textarea className="fld-input" rows={2} value={editingTask.notes} onChange={(e) => setEditingTask({ ...editingTask, notes: e.target.value })} placeholder="Notes" />
                    <div className="fld-task__edit-acts">
                      <button className="fld-btn fld-btn--primary" disabled={!editingTask.title.trim()} onClick={saveTaskEdit}>Save</button>
                      <button className="fld-btn fld-btn--ghost" onClick={() => setEditingTask(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className={'fld-task' + (t.is_complete ? ' fld-task--done' : '')} key={t.id}>
                    <input type="checkbox" checked={!!t.is_complete} disabled={done || t._pending} onChange={() => toggleTask(t)} />
                    <span className="fld-task__body">
                      <span className="fld-task__title">{t.title}{t._pending && <span className="fld-muted"> · syncing…</span>}</span>
                      {t.notes && <span className="fld-task__notes">{t.notes}</span>}
                    </span>
                    {!done && !t._pending && <button className="fld-task__edit-btn" onClick={() => setEditingTask({ id: t.id, title: t.title, notes: t.notes || '' })}>Edit</button>}
                  </div>
                )))}
              </div>
            )}
            {!done && (
              <div className="fld-noteadd">
                <input className="fld-input" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} placeholder="New task or issue…" />
                <button className="fld-btn" disabled={!newTask.title.trim()} onClick={addFieldTask}>Add</button>
              </div>
            )}
          </section>
        )}

        {/* Parts used */}
        <section className="fld-sec">
          <h2>Parts</h2>
          {(job.parts || []).length === 0 ? <p className="fld-muted">No parts yet.</p> : (
            <div className="fld-parts">
              {job.parts.map((p) => (
                <div className="fld-part" key={p.id}>
                  <div className="fld-part__title">{p.part_name}{p.sku ? ` · ${p.sku}` : ''}{p._pending && <span className="fld-muted"> · syncing…</span>}</div>
                  <div className="fld-part__meta">{Number(p.quantity)} × {money(p.unit_price)} = <strong>{money(p.total)}</strong>{p.ordered_from ? ` · from ${p.ordered_from}` : ''}</div>
                  {p.notes && <div className="fld-part__notes">{p.notes}</div>}
                  {p.billed && <div className="fld-part__billed">✓ Billed to customer</div>}
                </div>
              ))}
            </div>
          )}
          {!done && (showAddPart ? (
            <div className="fld-partadd">
              <input className="fld-input" placeholder="Part name" value={partForm.part_name} onChange={(e) => setPartForm({ ...partForm, part_name: e.target.value })} />
              <div className="fld-row2">
                <input className="fld-input" placeholder="SKU" value={partForm.sku} onChange={(e) => setPartForm({ ...partForm, sku: e.target.value })} />
                <input className="fld-input" placeholder="Ordered from" value={partForm.ordered_from} onChange={(e) => setPartForm({ ...partForm, ordered_from: e.target.value })} />
              </div>
              <div className="fld-row2">
                <input className="fld-input" type="number" inputMode="decimal" placeholder="Unit price" value={partForm.unit_price} onChange={(e) => setPartForm({ ...partForm, unit_price: e.target.value })} />
                <input className="fld-input" type="number" inputMode="numeric" placeholder="Qty" value={partForm.quantity} onChange={(e) => setPartForm({ ...partForm, quantity: e.target.value })} />
              </div>
              <input className="fld-input" placeholder="Notes" value={partForm.notes} onChange={(e) => setPartForm({ ...partForm, notes: e.target.value })} />
              <div className="fld-task__edit-acts">
                <button className="fld-btn fld-btn--primary" disabled={!partForm.part_name.trim()} onClick={addFieldPart}>Add part</button>
                <button className="fld-btn fld-btn--ghost" onClick={() => { setShowAddPart(false); setPartForm(emptyPartForm()); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="fld-btn fld-btn--ghost" onClick={() => setShowAddPart(true)}>+ Add part</button>
          ))}
        </section>

        {/* Notes */}
        <section className="fld-sec">
          <h2>Notes</h2>
          {!done && (
            <div className="fld-noteadd">
              <textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note from the field…" />
              <button className="fld-btn" disabled={busy || !noteText.trim()} onClick={addNote}>Add</button>
            </div>
          )}
          {(job.notes || []).length === 0 ? <p className="fld-muted">No notes yet.</p> : (
            <div className="fld-notes">
              {job.notes.map((n) => (
                <div className="fld-note" key={n.id}>
                  <div className="fld-note__body">{n.body}</div>
                  <div className="fld-note__meta">{n.author || 'Staff'} · {new Date(n.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Photos */}
        <section className="fld-sec">
          <h2>Photos</h2>
          {!done && (
            <div className="fld-photoadd">
              <div className="fld-seg">
                {['before', 'after', 'other'].map((k) => (
                  <button key={k} className={`fld-seg__b${photoKind === k ? ' on' : ''}`} onClick={() => setPhotoKind(k)}>{k}</button>
                ))}
              </div>
              <button className="fld-btn" disabled={busy} onClick={() => photoRef.current?.click()}>Take / add photo</button>
              <input ref={photoRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />
            </div>
          )}
          {(job.photos || []).length === 0 ? <p className="fld-muted">No photos yet.</p> : (
            <div className="fld-photos">
              {job.photos.map((p) => <FieldPhoto key={p.id} jobId={job.id} photo={p} onOpen={setLightbox} />)}
            </div>
          )}
        </section>

        {/* Completion */}
        <section className="fld-sec">
          <h2>Completion</h2>
          {done ? (
            <div className="fld-done">
              <div className="fld-done__badge">✓ Completed {job.completion?.completed_at ? `· ${date(job.completion.completed_at)}` : ''}</div>
              {job.completion?.summary && <p className="fld-done__sum">{job.completion.summary}</p>}
              {job.completion?.signature_data && (
                <div className="fld-sig-done">
                  <img src={job.completion.signature_data} alt="Customer signature" />
                  <div className="fld-muted">Signed by {job.completion.signer_name || '—'}{job.completion.signed_at ? ` · ${new Date(job.completion.signed_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}` : ''}</div>
                </div>
              )}
              <button className="fld-btn fld-btn--ghost" onClick={openComplete}>Re-sign / edit</button>
            </div>
          ) : (
            <div className="fld-complete-actions">
              {job.status === 'open' && <button className="fld-btn fld-btn--ghost" disabled={busy} onClick={start}>Start job</button>}
              <button className="fld-btn fld-btn--primary" disabled={busy} onClick={openComplete}>Complete &amp; get signature</button>
            </div>
          )}
        </section>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fld-overlay" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="fld-overlay__img" />
        </div>
      )}

      {/* Completion sheet */}
      {completing && (
        <div className="fld-sheet">
          <div className="fld-sheet__head">
            <button className="fld-link" onClick={() => setCompleting(false)}>Cancel</button>
            <div className="fld-top__title">Complete {job.job_number}</div>
            <span />
          </div>
          <div className="fld-sheet__body">
            <label className="fld-field">Work performed / summary
              <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Describe what was done…" />
            </label>
            <label className="fld-field">Customer name
              <input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Full name of the person signing" />
            </label>
            <div className="fld-field">
              <div className="fld-siglabel">Customer signature
                <button className="fld-link" onClick={() => sigRef.current?.clear()}>Clear</button>
              </div>
              <SignaturePad ref={sigRef} />
              <p className="fld-muted">By signing, the customer confirms the work above is complete and satisfactory.</p>
            </div>
            {compErr && <p className="fld-err">{compErr}</p>}
          </div>
          <div className="fld-sheet__foot">
            <button className="fld-btn fld-btn--primary fld-btn--lg" disabled={busy} onClick={submitComplete}>{busy ? 'Saving…' : 'Mark complete'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
